import * as React from 'react'
import PropTypes from 'prop-types'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import VideocamIcon from '@mui/icons-material/Videocam'
import VideoCallIcon from '@mui/icons-material/VideoCall'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import { Link as RouterLink } from 'react-router-dom'
import { Chip, Divider, Fab, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material'
import Feature from '../common/Feature'
import { useAuth } from '../../auth/AuthContext'
import env from '../../config/env'
import { getAdminStatus } from '../../service'

const navButtonSx = {
  color: 'text.primary',
  textTransform: 'none',
  fontWeight: 600
}

const AppBarContent = ({ username, groups = [], isAdmin = false, onSignOut }) => {
  const [profileAnchor, setProfileAnchor] = React.useState(null)
  const closeProfile = () => setProfileAnchor(null)
  return (
  <Box sx={{ flexGrow: 1, mb: 'var(--hl-appbar-gap)' }}>
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundImage: 'none',
        backgroundColor: 'rgba(10, 17, 23, .96)',
        backdropFilter: 'blur(12px)'
      }}
    >
      {/* 56px on phones, 48px from 900px up (--hl-appbar-height in index.css); dense avoids the default toolbar's 64px media query. */}
      <Toolbar variant="dense" sx={{ minHeight: 'var(--hl-appbar-height)' }}>
        <IconButton
          component={RouterLink}
          to="/"
          size="medium"
          color="primary"
          edge="start"
          aria-label="Highlights home"
          sx={{ mr: 1 }}
        >
          <VideocamIcon />
        </IconButton>

        <Typography
          variant="h6"
          component={RouterLink}
          to="/"
          sx={{
            mr: { xs: 1, md: 3 },
            display: { xs: 'none', sm: 'block' },
            fontWeight: 800,
            letterSpacing: '.08em',
            color: 'inherit',
            textDecoration: 'none'
          }}
        >
          HIGHLIGHTS
        </Typography>

        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
          <Button component={RouterLink} to="/" sx={navButtonSx}>
            Highlights
          </Button>
          <Button component={RouterLink} to="/players" sx={navButtonSx}>Players</Button>
          <Button component={RouterLink} to="/uploads" sx={navButtonSx}>
            Uploads
          </Button>
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Feature flag={Feature.STATS} username={username}>
            <Button component={RouterLink} to="/stats" sx={navButtonSx}>
              Stats
            </Button>
          </Feature>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {env.environment === 'dev'
          ? <Tooltip title={`Deployed build ${env.buildSha}. Groups: ${groups.length ? groups.join(', ') : 'none'}`}>
              <Chip
                label={`DEV ${String(env.buildSha || '').slice(0, 7)}`}
                size="small"
                variant="outlined"
                color="primary"
                sx={{
                  mr: 1.5,
                  display: { xs: 'none', sm: 'inline-flex' },
                  fontFamily: 'monospace',
                  fontSize: 13,
                  height: 24
                }}
              />
            </Tooltip>
          : null}

        <Tooltip title="Add a new upload">
          <Fab
            component={RouterLink}
            to="/new"
            color="success"
            size="small"
            aria-label="Add a new upload"
            sx={{ mr: 2, display: { xs: 'none', md: 'inline-flex' } }}
          >
            <VideoCallIcon />
          </Fab>
        </Tooltip>

        <Tooltip title="Profile">
          <IconButton
            id="profile-menu-button"
            color="inherit"
            onClick={event => setProfileAnchor(event.currentTarget)}
            aria-label="Profile menu"
            aria-controls={profileAnchor ? 'profile-menu' : undefined}
            aria-haspopup="menu"
            aria-expanded={Boolean(profileAnchor)}
          >
            <AccountCircleOutlinedIcon />
          </IconButton>
        </Tooltip>
        <Menu
          id="profile-menu"
          anchorEl={profileAnchor}
          open={Boolean(profileAnchor)}
          onClose={closeProfile}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          MenuListProps={{ 'aria-labelledby': 'profile-menu-button' }}
        >
          <Box sx={{ px: 2, py: 1, maxWidth: 280, overflowWrap: 'anywhere' }}>
            <Typography variant="caption" color="text.secondary">Signed in as</Typography>
            <Typography variant="body2">{username || 'Your account'}</Typography>
          </Box>
          <Divider />
          <MenuItem component={RouterLink} to="/players" onClick={closeProfile}><ListItemText>Players</ListItemText></MenuItem>
          <MenuItem component={RouterLink} to="/groups" onClick={closeProfile}>
            <ListItemIcon><GroupsOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Sharing groups</ListItemText>
          </MenuItem>
          {isAdmin
            ? <MenuItem component={RouterLink} to="/admin" onClick={closeProfile}>
                <ListItemIcon><AdminPanelSettingsOutlinedIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Admin</ListItemText>
              </MenuItem>
            : null}
          <Divider />
          <MenuItem onClick={() => { closeProfile(); onSignOut() }}>
            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Sign out</ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  </Box>
  )
}

AppBarContent.propTypes = {
  username: PropTypes.string,
  groups: PropTypes.array,
  isAdmin: PropTypes.bool,
  onSignOut: PropTypes.func.isRequired
}

const AuthenticatedHighlightsAppBar = () => {
  const { user, groups, signOut } = useAuth()
  const [isAdmin, setIsAdmin] = React.useState(false)

  React.useEffect(() => {
    let active = true
    if (!user) {
      setIsAdmin(false)
      return undefined
    }

    getAdminStatus()
      .then(result => {
        if (active) setIsAdmin(Boolean(result && result.is_admin))
      })
      .catch(() => {
        if (active) setIsAdmin(false)
      })

    return () => {
      active = false
    }
  }, [user])

  return (
    <AppBarContent
      username={user ? user.username : ''}
      groups={groups}
      isAdmin={isAdmin}
      onSignOut={signOut}
    />
  )
}

export default function HighlightsAppBar ({ testMode = false }) {
  if (testMode) {
    return <AppBarContent username="e2e-user" groups={['family']} isAdmin={false} onSignOut={() => {}} />
  }

  return <AuthenticatedHighlightsAppBar />
}

HighlightsAppBar.propTypes = {
  testMode: PropTypes.bool
}
